import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, ArrowRight, CheckCircle2, Clock, AlertTriangle, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const STATUS_META = {
  trialing: { label: 'On trial', color: 'bg-amber-100 text-amber-800', icon: Clock },
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  past_due: { label: 'Past due', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
  incomplete: { label: 'Incomplete', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
};

const PLAN_META = {
  starter: { label: 'Starter', price: 29 },
  pro: { label: 'Pro', price: 79 },
  business: { label: 'Business', price: 199 },
};

const fmt = (d) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'PPP'); } catch { return d; }
};

const BillingView = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: s }] = await Promise.all([
          api.get('/billing/config'),
          api.get('/billing/subscription'),
        ]);
        setConfig(cfg);
        setSub(s);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const { data } = await api.post('/billing/portal', {
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="billing-loading">
        <div className="spinner" />
      </div>
    );
  }

  const billingLive = !!config?.enabled;
  const status = sub?.subscription_status;
  const statusMeta = status ? STATUS_META[status] : null;
  const plan = sub?.subscription_plan ? PLAN_META[sub.subscription_plan] : null;

  return (
    <div className="space-y-6" data-testid="billing-view">
      <div>
        <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">Billing</h1>
        <p className="text-slate-500 mt-1">Manage your PROP VEIW subscription.</p>
      </div>

      {!billingLive ? (
        <Card className="p-8 border-amber-200 bg-amber-50/50">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
              <Clock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-manrope font-semibold text-lg text-slate-900">Billing is not active yet</h2>
              <p className="text-slate-600 mt-1">
                PROP VEIW is in free beta. When paid plans launch, we&apos;ll email you here to start your
                14-day free trial. Founder pricing locked in for early users.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => navigate('/pricing')} variant="outline" data-testid="billing-view-pricing">
                  View pricing
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : !sub?.has_active_subscription ? (
        <Card className="p-8">
          <h2 className="font-manrope font-semibold text-lg text-slate-900">No active subscription</h2>
          <p className="text-slate-600 mt-1">
            Pick a plan to get started. Every plan includes a 14-day free trial.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {Object.entries(PLAN_META).map(([id, meta]) => (
              <Button
                key={id}
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="h-auto py-4 flex flex-col items-start"
                data-testid={`billing-pick-${id}`}
              >
                <span className="font-semibold text-slate-900">{meta.label}</span>
                <span className="text-xs text-slate-500 mt-1">${meta.price}/mo</span>
              </Button>
            ))}
          </div>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-medium">Current plan</p>
                <h2 className="font-manrope font-bold text-2xl text-slate-900 mt-1">
                  {plan?.label || sub?.subscription_plan || 'Unknown'}
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  {plan ? `$${plan.price}/month` : ''}
                </p>
              </div>
              {statusMeta && (
                <Badge className={`${statusMeta.color} text-sm px-3 py-1`}>
                  <statusMeta.icon className="h-3.5 w-3.5 mr-1.5 inline" />
                  {statusMeta.label}
                </Badge>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-100">
              {status === 'trialing' && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-medium">Trial ends</p>
                  <p className="text-slate-900 font-medium mt-1">{fmt(sub.trial_ends_at)}</p>
                </div>
              )}
              {sub.current_period_end && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-medium">
                    {status === 'trialing' ? 'First charge on' : 'Renews on'}
                  </p>
                  <p className="text-slate-900 font-medium mt-1">{fmt(sub.current_period_end)}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={openPortal}
                disabled={portalLoading}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="billing-open-portal"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {portalLoading ? 'Opening…' : 'Manage subscription'}
                <ExternalLink className="h-3 w-3 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/pricing')}>
                Change plan
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              The Stripe Customer Portal lets you update your card, download invoices, or cancel.
            </p>
          </Card>

          <Card className="p-6 bg-slate-50">
            <h3 className="font-manrope font-semibold text-slate-900">Need a hand?</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Questions about billing, plan limits, or annual discounts? We respond within one business day.
            </p>
            <a
              href="mailto:billing@propveiw.com"
              className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 mt-4"
            >
              Email billing@propveiw.com
              <ArrowRight className="h-3 w-3 ml-1" />
            </a>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingView;
